import { useState, useEffect } from "react";

// Fired after a URL search-param write that ancestors may need to observe.
// `history.replaceState` (used by useFilterParam / useTableConfigParam) does NOT
// emit `popstate`, so without this signal a param written inside a child — e.g.
// the Table writing `?cols=` when a column is toggled — would re-render only the
// Table, never an ancestor that derives state from that param.
const URL_PARAMS_EVENT = "fs:url-params-change";

/**
 * Announce that a URL search param was written. Call right after any
 * `history.replaceState` that changes the query string.
 *
 * Deferred to a microtask so it is safe to call from inside a React state
 * updater (writing the param there is the existing pattern) — dispatching
 * synchronously would update a subscribed ancestor while the child is still
 * rendering, which React warns about.
 */
export function notifyUrlParamsChanged(): void {
  if (typeof window === "undefined") return;
  queueMicrotask(() => {
    // Guard for test environments that stub `window` without DOM event methods.
    if (typeof window.dispatchEvent === "function") {
      window.dispatchEvent(new Event(URL_PARAMS_EVENT));
    }
  });
}

/**
 * Re-render the calling component whenever a URL search param changes — both via
 * [notifyUrlParamsChanged] (replaceState writes) and the browser's back/forward
 * (`popstate`). Returns a counter that increments on each change for use as an
 * effect/memo dependency; callers that read params during render can ignore the
 * value and rely on the re-render alone.
 */
export function useUrlParamsTick(): number {
  const [tick, setTick] = useState(0);
  useEffect(() => {
    function onChange() {
      setTick((t) => t + 1);
    }
    window.addEventListener(URL_PARAMS_EVENT, onChange);
    window.addEventListener("popstate", onChange);
    return () => {
      window.removeEventListener(URL_PARAMS_EVENT, onChange);
      window.removeEventListener("popstate", onChange);
    };
  }, []);
  return tick;
}
